/**
 * Account Setup Page
 *
 * Main page for completing account setup from email link.
 * Shows user details and form to complete account setup.
 *
 * URL: /account-setup?user-id=123&token=abc
 *
 * @module pages/auth
 */

import { needsAccountSetup } from '@butter/library/shared';
import { useState, useEffect, useCallback } from 'react';
// Never react-router's useNavigate here: this app carries tenant-handle + club-id in the query
// string, and a bare path drops them — MissingParamsGuard then blocks the whole app.
import { useTenantNavigate, useTenantSearchParams } from '~/lib/navigation';
import { SetupHeader, AccountSetupForm, AccountDetailsCard, AccountSetupWaiverCard, ACCOUNT_SETUP_FORM_ID } from './components';
import { butterToast } from '@butter/ui/patterns';
import { Checkbox } from '@butter/ui/primitives/inputs';
import { useUserQuery } from '~/services/user';
import { authService } from '~/services/auth';
import { buildUrl, QueryParams } from '~/lib/queryParams';
import { formatDisplayName, isPlaceholderEmail, isPlaceholderName, realEmail } from '@butter/library/shared';
import { TenantLink } from '~/lib/navigation';
import { PaymentProvider, useSetDefaultPaymentMethod, reportPaymentClientError, useCardSave, CardEntryForm, isPaymentSaveError } from '~/features/payment';
import { useWaiverCapture } from '~/features/waivers';
import { detectPaymentProvider, getPaymentSettings } from '~/lib/utils/paymentProvider';
import { describePaymentMethod } from '@butter/ui/domains/billing';
// A membership start date is a club DAY. toLocaleDateString renders it in the viewer's
// timezone, which shows the wrong day for any pre-anchor row west of the club.
import { asTimezone, asUtcDate, formatClubDate } from '@butter/library/datetime';
import { phoneForDisplay } from '@butter/library/phone';
import { DEFAULT_CLUB_COUNTRY } from '@lgc/database/types/domains/club';

type FormStatus = 'idle' | 'validating' | 'ready' | 'error';

const REQUIRE_PAYMENT_FOR_MEMBERSHIP = true;

/**
 * Account Setup Page
 *
 * Flow:
 * 1. Extract user-id and token from URL
 * 2. Validate setup token
 * 3. Fetch user data
 * 4. Show form for account completion
 * 5. On success, navigate to success page
 */
export function AccountSetupPage() {
  const [searchParams] = useTenantSearchParams();
  const navigate = useTenantNavigate();

  // Extract URL params using standard QueryParams
  const userId = searchParams.get(QueryParams.USER_ID);
  const setupToken = searchParams.get(QueryParams.TOKEN) || '';
  const clubId = searchParams.get(QueryParams.CLUB_ID);
  const tenant = searchParams.get(QueryParams.TENANT_HANDLE);

  // Form state - removed status/errorMessage/tokenValidated as they are now derived

  const [confirmDetails, setConfirmDetails] = useState(false);
  const [paymentSaved, setPaymentSaved] = useState(false);
  // Whether the "add a different card" disclosure is open, for members who arrived with
  // cards already on file. Gates both the render and the SetupIntent fetch below.
  const [replaceCardOpen, setReplaceCardOpen] = useState(false);
  // Owned here, not by the form: the submit button sits in the action bar below the
  // grid (see the comment there), so the page needs the form's in-flight state.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // True across the whole card save (Stripe confirm + persist) — see CardEntryForm's
  // onSubmittingChange. Submit runs completeAccountSetup → login() → navigate(), any of
  // which unmounts the page, stranding a spent single-use SetupIntent mid-confirm. When
  // payment is OPTIONAL `isPaymentSatisfied` is true from mount, so nothing else stops
  // the user pressing Complete Setup during a save.
  const [savingCard, setSavingCard] = useState(false);

  // tenant-handle comes directly from searchParams — appRuntime can be stale on this page
  // since users arrive via a fresh email link load, not SPA navigation.
  const setDefaultPaymentMethod = useSetDefaultPaymentMethod({ tenantHandle: tenant || undefined });

  // Fetch user data
  const {
    data: user,
    isLoading: isUserLoading,
    error: userError
  } = useUserQuery({
    userId,
    enabled: !!userId,
  });

  // Home club, not `clubs[0]`: `buildUserClubsPayload` has no ORDER BY, so "first" is
  // arbitrary — for a multi-club member that picked a random club's payment provider.
  // Mirrors the same fix in mobile's `deriveAccountSetupState`.
  const homeClub = user?.clubs?.find(c => c.isHome) ?? user?.clubs?.[0] ?? null;
  const effectiveClubId = clubId || homeClub?.id?.toString();

  // Payment provider detection — prefer the club matching this setup flow's effectiveClubId
  const clubForPayment = effectiveClubId
    ? (user?.clubs?.find(c => String(c.id) === String(effectiveClubId)) ?? homeClub)
    : homeClub;
  // Typed settings for this flow's club — the loose `getPaymentSettings` param
  // accepts a `UserClub`, so no cast is needed (matches booking/subscription).
  const paymentSettingsForClub = getPaymentSettings(clubForPayment);
  // The default region for a phone typed without a country code — this flow's club.
  const phoneRegion = clubForPayment?.country ?? DEFAULT_CLUB_COUNTRY;
  const paymentProvider = detectPaymentProvider(paymentSettingsForClub);

  const userHasPayment = user?.hasPayment ?? false;
  const showPaymentForm = !userHasPayment;
  // Imported/active cards (default first) — surfaced by the account-setup fetch so
  // a migrated member can pick which one bills their membership.
  const importedMethods = user?.paymentMethods ?? [];
  const paymentIsRequired = REQUIRE_PAYMENT_FOR_MEMBERSHIP && (user?.requiresPayment ?? false) && !userHasPayment;
  const isPaymentSatisfied = userHasPayment || !paymentIsRequired || paymentSaved;

  // The club's waiver, captured here and posted the moment activation mints a session.
  // Invited and imported members are the reason this exists: the entry gate exempts
  // anyone enrolled before the waiver was published, so for them this page is the only
  // place the club ever asks. Fail-closed — see the submit gate below.
  // `clubForPayment`, not the raw `club-id` param: payment already falls back to the
  // home club when the link names a club the member is not in, and the waiver has to
  // follow the same club — otherwise they sign one club's document while being set up
  // at another.
  const waiver = useWaiverCapture(clubForPayment?.id ?? null);

  // Numeric user id for the card-save endpoints.
  const cardUserId =
    user?.id == null
      ? undefined
      : typeof user.id === 'number'
        ? user.id
        : parseInt(String(user.id));

  // Stripe SetupIntent wiring + the shared confirm/save primitive.
  const {
    stripeSetupIntent,
    stripeSetupIntentLoading,
    stripeSetupIntentError,
    retryStripeSetupIntent,
    saveCard,
  } = useCardSave({
    userId: cardUserId,
    clubId: effectiveClubId ? Number(effectiveClubId) : undefined,
    tenantHandle: tenant || undefined,
    paymentProvider,
    // Tied to whether a card form is RENDERED, not to whether payment is outstanding.
    // Those differ for the case that matters: when payment is optional,
    // `isPaymentSatisfied` is true from mount, so gating on it meant a member with no
    // card on file saw the "Payment Method (Optional)" section with no form inside it —
    // the SetupIntent was never requested, so the Stripe branch had no intent, no
    // loading state and no error to render. `showPaymentForm` is the same condition
    // that renders the section; `replaceCardOpen` covers the disclosure a member with
    // imported cards opens. Matches how the booking and subscription steps gate theirs.
    stripeSetupEnabled: (showPaymentForm || replaceCardOpen) && paymentProvider === 'stripe',
  });

  const handlePaymentSuccess = async (providerToken: string) => {
    if (!user?.id || !effectiveClubId || !paymentProvider) return;
    try {
      await saveCard(providerToken);
      setPaymentSaved(true);
    } catch (err) {
      // saveCard's Stripe branch retries the single-use intent + rethrows; neither
      // hook toasts on its own — surface the failure so the user isn't left staring
      // at a form that silently didn't save. paymentSaved stays false.
      reportPaymentClientError('save_card', err, {
        kind: 'client_flow',
        surface: 'web_account_setup',
        extra: { provider: paymentProvider },
      });
      // Prefer the API's own reason ("Your card was declined", "We couldn't
      // verify that card…") over a fixed line the member can't act on. Falls
      // back for a client-state guard, which has no server message.
      butterToast.error(
        isPaymentSaveError(err) ? err.message : "We couldn't save your card. Please try again.",
      );
    }
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId: number) => {
    if (!cardUserId) return;
    try {
      await setDefaultPaymentMethod.mutateAsync({ userId: cardUserId, paymentMethodId });
    } catch (err) {
      // The hook doesn't toast; surface the failure. userDetail invalidation on
      // success re-renders the list with the moved default.
      reportPaymentClientError('set_default_payment_method', err, {
        kind: 'mutation',
        surface: 'web_account_setup',
      });
      butterToast.error("We couldn't update your default card. Please try again.");
    }
  };

  // Token validation state - start with isValidating=true to show loading immediately
  // We initialize with isValidating=true to avoid the flash of content before validation
  const [tokenValidationState, setTokenValidationState] = useState<{
    isValidating: boolean;
    isValidated: boolean;
    isValid: boolean;
    errorMessage: string | null;
    resent: boolean;
  }>({
    isValidating: true, // Start as validating
    isValidated: false,
    isValid: false,
    errorMessage: null,
    resent: false,
  });

  // Token validation function - called from effect with proper async handling
  const validateToken = useCallback(async () => {
    if (!setupToken) {
      // No token is okay - we'll still show the form
      return { isValid: true, errorMessage: null, resent: false };
    }

    try {
      // On an expired token the API re-sends a fresh invite to the token's owner
      // (resolved server-side) and reports `resent`.
      const result = await authService.validateSetupToken(setupToken);

      if (!result.valid) {
        return {
          isValid: false,
          errorMessage: result.error || 'Invalid or expired setup token',
          resent: result.resent ?? false,
        };
      }

      return { isValid: true, errorMessage: null, resent: false };
    } catch (error) {
      console.error('[AccountSetupPage] Token validation error:', error);
      return {
        isValid: false,
        errorMessage: 'Failed to validate setup token. Please try again.',
        resent: false,
      };
    }
  }, [setupToken]);

  // Validate token on mount (only when we have userId)
  // The initial state has isValidating=true, so we just need to trigger the validation
  // and update with results. This avoids calling setState synchronously.
  useEffect(() => {
    // Skip validation if no userId - the error is derived from userId check
    if (!userId || tokenValidationState.isValidated) {
      return;
    }

    let cancelled = false;

    validateToken().then(result => {
      if (!cancelled) {
        setTokenValidationState({
          isValidating: false,
          isValidated: true,
          isValid: result.isValid,
          errorMessage: result.errorMessage,
          resent: result.resent,
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [userId, tokenValidationState.isValidated, validateToken]);

  // Derive status from validation state and other conditions
  // If no userId, immediately show error (no need to wait for validation)
  const derivedStatus: FormStatus = !userId
    ? 'error'
    : userError
      ? 'error'
      : tokenValidationState.isValidating && !tokenValidationState.isValidated
        ? 'validating'
        : !tokenValidationState.isValidated
          ? 'validating' // Show loading while validation hasn't started yet
          : tokenValidationState.isValid
            ? 'ready'
            : 'error';

  // Derive error message
  const derivedErrorMessage = !userId
    ? 'Missing user ID. Please use the link from your email.'
    : userError
      ? (userError as { userMessage?: string }).userMessage ||
        'Failed to load user data. Please try again later.'
      : tokenValidationState.errorMessage;

  // Check if account is already set up.
  //
  // `pending` = invited, never finished. `incomplete` = self-created via the booking
  // flow, also never finished (carries no invitation token). Both still need the form;
  // anything else means the account is live and the form would be wrong.
  const isAccountAlreadySetup = Boolean(user) && !needsAccountSetup(user!);

  // Placeholder-email users (phone-only Golf918 imports) must supply a real email at setup
  const requireEmailInput = isPlaceholderEmail(user?.email ?? '');

  // Handle form completion
  const handleComplete = ({ signInRequired }: { signInRequired: boolean }) => {
    // Built explicitly rather than relying on the navigate wrapper's preservation: club-id may
    // be absent from the current URL, in which case `effectiveClubId` falls back to the user's
    // first club. Explicit params win over preserved ones in `buildUrl`, so that enrichment
    // survives the wrapper.
    const successUrl = buildUrl('/account-setup-success', {
      [QueryParams.TENANT_HANDLE]: searchParams.get(QueryParams.TENANT_HANDLE),
      [QueryParams.CLUB_ID]: effectiveClubId,
    });
    // An existing Butter login: setup created no session, so the success page says how to
    // get in — the member signs in with the password they already use.
    navigate(
      successUrl,
      signInRequired
        ? { state: { message: 'Your account is ready. Sign in with the email and password you already use with Butter to get started.' } }
        : undefined,
    );
  };

  // Loading state
  if (isUserLoading || derivedStatus === 'validating') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-secondary">
        <div className="max-w-md w-full mx-auto p-8">
          <SetupHeader
            title="Account Setup"
            subtitle="Loading your account information..."
            tenantHandle={tenant}
          />
          <div className="mt-6 flex items-center justify-center gap-2 text-theme-600">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-theme-600 border-t-transparent" />
            <span>Please wait...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state — an expired/invalid invitation token that triggered a fresh
  // invite renders as a friendly "check your email", not a dead-end error.
  //
  // `!isAccountAlreadySetup` keeps a spent token from being reported as a failure to
  // someone who spent it themselves by finishing setup. Their token necessarily fails
  // validation (it is single-use), so without this guard the error branch below claimed
  // every one of them before the "Account Already Set Up" view further down could ever
  // render — the member's own success looked identical to a broken link. Account status
  // is the authoritative signal here and it wins; the token still speaks for users who
  // have NOT finished setup, where an expired link is genuinely the problem.
  if (derivedStatus === 'error' && !isAccountAlreadySetup) {
    if (tokenValidationState.resent) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-bg-secondary">
          <div className="max-w-md w-full mx-auto p-8">
            <SetupHeader
              title="Account Setup"
              subtitle="Your link expired"
              tenantHandle={tenant}
            />
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800">
                This invitation link has expired. A new invitation has been sent to your
                email{realEmail(user?.email) ? ` (${realEmail(user?.email)})` : ''} — please check your inbox to
                finish setting up your account.
              </p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-secondary">
        <div className="max-w-md w-full mx-auto p-8">
          <SetupHeader
            title="Account Setup"
            subtitle="We encountered a problem"
            tenantHandle={tenant}
          />
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{derivedErrorMessage}</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-theme-600 hover:text-theme-700 text-sm"
          >
            ← Go back
          </button>
        </div>
      </div>
    );
  }

  // Account already setup state
  if (isAccountAlreadySetup) {
    return (
      <div className="min-h-screen bg-bg-secondary py-12">
        <div className="max-w-2xl mx-auto px-4">
          <SetupHeader
            title="Account Setup"
            firstName={user?.firstName}
            tenantHandle={tenant}
          />

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Account Already Set Up</h3>
                <p className="text-blue-800 text-sm">
                  Your account has already been configured. Below are your current account details.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-fg-primary mb-4">Your Account Details</h2>

            <div className="space-y-4 text-sm">
              <div className="border-b border-gray-100 pb-3">
                <span className="text-fg-quaternary">Name: </span>
                <span className="text-fg-primary font-medium">
                  {formatDisplayName(user?.firstName, user?.lastName, 'Not provided')}
                </span>
              </div>
              <div className="border-b border-gray-100 pb-3">
                <span className="text-fg-quaternary">Email: </span>
                <span className="text-fg-primary font-medium">{realEmail(user?.email) ?? 'Not provided'}</span>
              </div>
              <div>
                <span className="text-fg-quaternary">Phone: </span>
                <span className="text-fg-primary font-medium">{phoneForDisplay(user?.phone, phoneRegion).label || 'Not provided'}</span>
              </div>
            </div>
          </div>

          <TenantLink
            to='/'
            className="block mt-6 w-full py-3 px-4 bg-theme-600 text-white text-center font-medium rounded-lg text-interactive:bg-theme-700 transition-colors"
          >
            Continue to Booking
          </TenantLink>
        </div>
      </div>
    );
  }

  // Main form view
  return (
    <div className="min-h-screen bg-bg-secondary py-12">
      <div className="max-w-4xl mx-auto px-4">
        <SetupHeader
          title="Account Setup"
          firstName={user?.firstName}
          subtitle="Complete your profile to get started."
          tenantHandle={tenant}
        />

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Form */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-fg-primary mb-4">
              Complete Your Profile
            </h2>

            {user && (
              <AccountSetupForm
                userId={user.id}
                email={requireEmailInput ? '' : (user.email ?? '')}
                requireEmailInput={requireEmailInput}
                existingLogin={user.existingLogin ?? false}
                initialFirstName={isPlaceholderName(user.firstName ?? '') ? '' : user.firstName}
                initialLastName={isPlaceholderName(user.lastName ?? '') ? '' : user.lastName}
                initialPhone={user.phone}
                clubId={effectiveClubId}
                phoneRegion={phoneRegion}
                setupToken={setupToken}
                onSubmittingChange={setIsSubmitting}
                getWaiverConsents={waiver.capturedPayload}
                onComplete={handleComplete}
              />
            )}
          </div>

          {/* Right Column - Account Details */}
          <div className="space-y-6">
            <AccountDetailsCard user={user ?? null} isLoading={isUserLoading} />

            {/* Subscription Carry-Over Card */}
            {(user?.activeMembership || user?.activePass) && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-base font-semibold text-fg-primary mb-3">Your Subscription</h3>
                <p className="text-sm text-fg-quaternary mb-4">
                  The following is being carried over to your new account.
                </p>

                {user.activeMembership && (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 h-2 w-2 rounded-full bg-theme-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-fg-primary">{user.activeMembership.name}</p>
                      <p className="text-xs text-fg-quaternary mt-0.5">
                        {user.activeMembership.isUpcoming && user.activeMembership.startDate
                          ? `Starting ${formatClubDate(asUtcDate(new Date(user.activeMembership.startDate)), clubForPayment?.timezone ?? asTimezone('UTC'), 'MMMM d, yyyy')}`
                          : 'Active membership'}
                        {user.activeMembership.isFree && ' · Free'}
                      </p>
                    </div>
                  </div>
                )}

                {user.activePass && (
                  <div className="flex items-start gap-3 mt-3">
                    <div className="mt-0.5 h-2 w-2 rounded-full bg-theme-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-fg-primary">{user.activePass.name}</p>
                      <p className="text-xs text-fg-quaternary mt-0.5">Active pass</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Method Section */}
            {showPaymentForm && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-fg-primary">
                    {paymentIsRequired ? 'Payment Method (Required)' : 'Payment Method (Optional)'}
                  </h3>
                  <p className="text-sm text-fg-quaternary mt-1">
                    {paymentIsRequired
                      ? 'A payment method is required for your membership billing.'
                      : 'Optionally add a payment method to save time when booking.'}
                  </p>
                </div>

                {paymentSaved ? (
                  <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                    <svg aria-hidden="true" className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium">Payment method saved.</span>
                  </div>
                ) : (
                  <PaymentProvider>
                    <CardEntryForm
                      paymentProvider={paymentProvider}
                      stripeSetupIntent={stripeSetupIntent}
                      stripeSetupIntentLoading={stripeSetupIntentLoading}
                      stripeSetupIntentError={stripeSetupIntentError}
                      retryStripeSetupIntent={retryStripeSetupIntent}
                      paymentSettings={paymentSettingsForClub}
                      onCardSuccess={handlePaymentSuccess}
                      onSubmittingChange={setSavingCard}
                      errorLabel="AccountSetupPage"
                    />
                  </PaymentProvider>
                )}
              </div>
            )}

            {/* Imported Payment Methods — pick the default (migration flow).
                Shown when the member arrived with cards already on file; the
                per-row star sets which one bills their membership. */}
            {userHasPayment && importedMethods.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-fg-primary">Payment Methods</h3>
                  <p className="text-sm text-fg-quaternary mt-1">
                    Choose your default card for membership billing.
                  </p>
                </div>

                <ul className="space-y-2">
                  {importedMethods.map((method) => {
                    const desc = describePaymentMethod({
                      methodType: method.methodType,
                      cardBrand: method.brand,
                      last4: method.last4,
                      expMonth: method.expMonth,
                      expYear: method.expYear,
                      bankName: method.bankName,
                      bankAccountType: method.bankAccountType,
                    });
                    return (
                    <li
                      key={method.id}
                      className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5"
                    >
                      <span className="text-sm text-fg-primary">
                        {desc.label}
                        {desc.sublabel ? (
                          <span className="text-fg-quaternary"> · {desc.sublabel}</span>
                        ) : null}
                      </span>

                      {method.isDefault ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-theme-700">
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                          </svg>
                          Default
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleSetDefaultPaymentMethod(method.id)}
                          disabled={setDefaultPaymentMethod.isPending}
                          title="Set as Default"
                          aria-label={`Set ${desc.name} ending ${desc.last4 ?? ''} as default`}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-fg-quaternary text-interactive:text-theme-700 disabled:opacity-50"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                          </svg>
                          Set as default
                        </button>
                      )}
                    </li>
                    );
                  })}
                </ul>

                {/* An imported card is the member's only signal that the thing about to
                    bill their membership is expired or sits under the club's previous
                    provider — `hasPayment` checks neither. Offer a replacement alongside
                    it, collapsed so it stays out of the way when the card is fine. */}
                {!paymentSaved && (
                  <details className="mt-4" onToggle={(e) => setReplaceCardOpen(e.currentTarget.open)}>
                    <summary className="cursor-pointer text-sm font-medium text-theme-600 text-interactive:text-theme-700">
                      Add a different card
                    </summary>
                    {/* Mounted only once opened: a closed <details> still renders its
                        children, which would mint a Stripe SetupIntent on arrival for
                        every member with cards and mount Elements into a display:none
                        container. Opening it is also what enables the SetupIntent fetch
                        (see stripeSetupEnabled). */}
                    {replaceCardOpen && (
                    <div className="mt-3">
                      <PaymentProvider>
                        <CardEntryForm
                          paymentProvider={paymentProvider}
                          stripeSetupIntent={stripeSetupIntent}
                          stripeSetupIntentLoading={stripeSetupIntentLoading}
                          stripeSetupIntentError={stripeSetupIntentError}
                          retryStripeSetupIntent={retryStripeSetupIntent}
                          paymentSettings={paymentSettingsForClub}
                          onCardSuccess={handlePaymentSuccess}
                          onSubmittingChange={setSavingCard}
                          errorLabel="AccountSetupPage"
                        />
                      </PaymentProvider>
                    </div>
                    )}
                  </details>
                )}
              </div>
            )}

            {/*
              Waiver — above Confirm Details on purpose. "I confirm that all the details
              above are correct" has to be the last thing before the action bar, so the
              document it implicitly covers must render before it.
            */}
            <AccountSetupWaiverCard
              clubId={clubForPayment?.id ?? null}
              capture={waiver}
              disabled={isSubmitting || savingCard}
            />

            {/*
              Confirm Details — deliberately the same control, gap and label tone as the
              waiver's agreement row directly above it. Now that neither sits in a card,
              they read as one pair of consents, and a raw <input> beside the Checkbox
              primitive showed it: a different box, half a step lower, in lighter text.
            */}
            <div className="flex items-start gap-2.5">
              <Checkbox
                id="confirm-details"
                checked={confirmDetails}
                disabled={savingCard}
                onCheckedChange={(checked) => setConfirmDetails(checked === true)}
                className="mt-0.5"
              />
              <label htmlFor="confirm-details" className="text-sm text-butter-gray-700">
                I confirm that all the details above are correct and I wish to proceed with the account setup.
              </label>
            </div>
          </div>
        </div>

        {/*
          Action bar — deliberately BELOW the grid rather than inside the form.

          The grid is `grid-cols-1 lg:grid-cols-2`, so on a narrow screen it collapses
          and everything in the right column (payment, imported cards, confirm-details)
          renders after the form. With the submit button still inside the form it landed
          ABOVE the very gates that enable it, and the disabled state had no visible
          cause. Sitting here, the reading order is fields -> gates -> submit at every
          width.

          `form={ACCOUNT_SETUP_FORM_ID}` is what keeps this a real submit button for that
          form despite being outside it — native validation and Enter-to-submit included.
        */}
        <div className="mt-6 text-center">
          {!isPaymentSatisfied && (
            <p className="mb-2 text-sm text-amber-600">Please add a payment method to continue.</p>
          )}
          {/*
            Two messages for two states. Telling someone to sign a waiver that never
            rendered is a dead end — when the list could not be loaded, the card above
            shows the retry and this says so.
          */}
          {isPaymentSatisfied && !waiver.satisfied && (waiver.answerMissing || waiver.hasAnswer) && (
            <p className="mb-2 text-sm text-amber-600">
              {waiver.answerMissing
                ? "We couldn't confirm this club's waiver requirements. Please try again."
                : // "sign" only when a signature is actually required — a checkbox-only
                  // waiver asks the member to accept, and telling them to sign one sends
                  // them hunting for a pad that is not there. Gated on `hasAnswer` above,
                  // so no line — and no empty amber gap — while the list is loading.
                  waiver.waivers.some((w) => w.requireSignature)
                  ? 'Please review and sign the waiver to continue.'
                  : 'Please review and accept the waiver to continue.'}
            </p>
          )}
          <button
            type="submit"
            form={ACCOUNT_SETUP_FORM_ID}
            disabled={isSubmitting || savingCard || !confirmDetails || !isPaymentSatisfied || !waiver.satisfied || !user}
            className="w-full max-w-2xl py-3 px-4 bg-theme-600 text-white font-medium rounded-lg hover:bg-theme-700 focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Setting up account...' : 'Complete Setup'}
          </button>
        </div>
      </div>
    </div>
  );
}
